/**
 * URLManager - Handles URL parameters for project activation and state management
 * Manages browser URL state and coordinates project activation via URL params
 */
class URLManager {
    static CONFIG = {
        // URL parameter name for project slug
        URL_PARAM_PROJECT: 'project'
    };

    constructor(uiManager, markerManager, dataManager) {
        if (!uiManager || !markerManager || !dataManager) {
            throw new Error('URLManager requires uiManager, markerManager, and dataManager');
        }
        this.uiManager = uiManager;
        this.markerManager = markerManager;
        this.dataManager = dataManager;
        this.isReady = false;
        this.isInitialLoad = true; // Track if this is the first URL check
    }
    /**
     * Initialize URL Manager
     * Sets up URL parameter checking and popstate listener for browser back/forward
     */
    initialize() {
        // Check URL parameters for initial project activation
        this._checkURLParameters();
        
        // Listen for browser back/forward navigation
        this._setupPopStateListener();
        
        this.isReady = true;
    }

    /**
     * Set up popstate listener for browser back/forward buttons
     * @private
     */
    _setupPopStateListener() {
        window.addEventListener('popstate', () => {
            this._checkURLParameters();
        });
    }

    /**
     * Check URL parameters for project activation
     * @private
     */
    _checkURLParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        const projectSlug = urlParams.get(URLManager.CONFIG.URL_PARAM_PROJECT);

        if (projectSlug) {
            // Activate the project, passing initial load flag
            this.activateProjectBySlug(projectSlug, { isInitialLoad: this.isInitialLoad });
        } else {
            // No project in URL - deactivate any active project
            this._deactivateCurrentProject();
        }
        
        // After first check, subsequent checks (popstate) should allow flying
        this.isInitialLoad = false;
    }

    /**
     * Deactivate current project
     * @private
     */
    _deactivateCurrentProject() {
        // Deactivate marker
        if (this.markerManager) {
            this.markerManager.deactivateAllMarkers();
        }

        // Deactivate project in UI
        if (this.uiManager && this.uiManager.activeProjectSlug) {
            this.uiManager.deactivateProject(this.uiManager.activeProjectSlug);
        }

        // Close panels
        if (this.uiManager) {
            this.uiManager.closeProjectsPanel();
        }
    }

    /**
     * Activate a project by slug
     * Coordinates UI and marker activation
     * @param {string} projectSlug - Project slug to activate
     * @param {Object} options - Activation options
     * @param {boolean} options.isInitialLoad - Whether this is from initial URL load
     * @public
     */
    activateProjectBySlug(projectSlug, options = {}) {
        if (!projectSlug) {
            return;
        }

        // Validate project exists in data
        const projectExists = this.dataManager.allLocations.some(
            location => location.properties.id === projectSlug
        );

        if (!projectExists) {
            console.warn(`⚠️ Project not found: ${projectSlug}`);
            return;
        }

        // Open projects panel
        if (this.uiManager) {
            this.uiManager.openProjectsPanel();
        }

        // Activate project in UI (which will also activate marker)
        if (this.uiManager) {
            this.uiManager.activateProject(projectSlug, options);
        }
    }

    /**
     * Update URL with project slug
     * @param {string} projectSlug - Project slug to add to URL (null to remove)
     * @public
     */
    updateURL(projectSlug) {
        if (projectSlug) {
            // Add project parameter to URL
            const url = new URL(window.location);
            url.searchParams.set(URLManager.CONFIG.URL_PARAM_PROJECT, projectSlug);
            window.history.pushState({}, '', url);
        } else {
            // Remove all query parameters and reset to base URL
            const baseUrl = window.location.origin + window.location.pathname;
            window.history.pushState({}, '', baseUrl);
        }
    }

    /**
     * Hook into UIManager to update URL when projects are activated/deactivated
     * Should be called after UIManager is initialized
     * @public
     */
    hookIntoUIManager() {
        if (!this.uiManager) {
            return;
        }

        // Store original methods
        const originalActivate = this.uiManager.activateProject.bind(this.uiManager);
        const originalDeactivate = this.uiManager.deactivateProject.bind(this.uiManager);

        // Wrap activateProject
        this.uiManager.activateProject = (projectSlug) => {
            originalActivate(projectSlug);
            this.updateURL(projectSlug);
        };

        // Wrap deactivateProject
        this.uiManager.deactivateProject = (projectSlug) => {
            originalDeactivate(projectSlug);
            this.updateURL(null);
        };
    }
}
// ! EXPORT
export default URLManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.URLManager = URLManager;
}
